/**
 * File: messageTemplates.ts
 * Path: packages/config/src/leads/messageTemplates.ts
 * Description: The WhatsApp messages the CRM types out for Liam (the payment
 * reminder, the two first messages, the audit message). Each has a standard
 * wording; Settings > Templates can replace it. A template is plain text
 * with {words in brackets} that are filled in for each person. Emails are
 * not here: those live with the email templates.
 */

export const MESSAGE_TEMPLATE_IDS = ["paymentReminder", "openerWebsite", "openerNoWebsite", "auditLink"] as const;
export type MessageTemplateId = (typeof MESSAGE_TEMPLATE_IDS)[number];

export const MESSAGE_TEMPLATE_MAX_LENGTH = 1500;

export interface MessageTemplateInfo {
  label: string;
  /** Where the message is made, in plain words. */
  where: string;
  /** The {words} it may use, without the brackets. */
  words: readonly string[];
  /** Words the message must keep (the reference, the opt-out). */
  required: readonly string[];
  standard: string;
}

const HELLO = "Hi {first name}, it's {my name} from IDK Solutions.";
const STOP = "Reply STOP and I won't message again.";

export const MESSAGE_TEMPLATES: Record<MessageTemplateId, MessageTemplateInfo> = {
  paymentReminder: {
    label: "Payment reminder",
    where: "WhatsApp, from Money, Owed",
    words: ["first name", "payment", "amount", "due date", "reference", "sender"],
    required: ["reference"],
    standard:
      "Hi {first name}, a quick reminder that {payment} ({amount}) was due on {due date}. " +
      "Please use {reference} as the payment reference. Let me know if anything is unclear. {sender}",
  },
  openerWebsite: {
    label: "First message, business with a website",
    where: "WhatsApp opener from the Pipeline",
    words: ["first name", "business", "my name"],
    required: ["STOP"],
    standard: [
      HELLO,
      "I checked {business}'s website and found a few things that are likely costing you enquiries. Can I send you a free audit of your site?",
      STOP,
    ].join("\n\n"),
  },
  openerNoWebsite: {
    label: "First message, business without a website",
    where: "WhatsApp opener from the Pipeline",
    words: ["first name", "business", "my name", "price"],
    required: ["STOP"],
    standard: [
      HELLO,
      "I found {business} on Google Maps but no website, so people searching online can't book you.",
      "I build websites with booking built in for a fixed {price}, live in about a month. Want me to send you examples?",
      STOP,
    ].join("\n\n"),
  },
  auditLink: {
    label: "Free audit message",
    where: "WhatsApp, after they say yes to the first message",
    words: ["first name", "audit link", "my name"],
    required: ["audit link"],
    standard: [
      "Thanks {first name}. Here is the free audit of your site: {audit link}",
      "I fix what it finds at a fixed price, and you get more enquiries within 60 days or I keep working for free. Want me to?",
      "{my name}, IDK Solutions",
    ].join("\n\n"),
  },
};

/** Only the templates Liam changed: id → his wording. */
export type MessageTemplateOverrides = Partial<Record<MessageTemplateId, string>>;

const WORD = /\{([^{}\n]{1,30})\}/g;

/** Fills in the {words}; any word it has no value for is left as typed. */
export const fillTemplate = (text: string, values: Readonly<Record<string, string>>): string =>
  text.replace(WORD, (whole, word: string) => values[word.trim().toLowerCase()] ?? whole);

/** The wording to use: Liam's, or the standard one. */
export const templateText = (id: MessageTemplateId, overrides?: MessageTemplateOverrides): string => {
  const own = overrides?.[id];
  return own && own.trim() ? own : MESSAGE_TEMPLATES[id].standard;
};

/** What is wrong with a wording, in plain words; empty when it is fine to save. */
export const templateProblems = (id: MessageTemplateId, text: string): string[] => {
  const info = MESSAGE_TEMPLATES[id];
  const problems: string[] = [];
  if (text.length > MESSAGE_TEMPLATE_MAX_LENGTH) problems.push(`Keep it under ${MESSAGE_TEMPLATE_MAX_LENGTH} characters.`);
  const used = [...text.matchAll(WORD)].map((m) => (m[1] ?? "").trim().toLowerCase());
  const unknown = [...new Set(used.filter((w) => !info.words.includes(w)))];
  if (unknown.length > 0) problems.push(`These words aren't known here: ${unknown.map((w) => `{${w}}`).join(", ")}.`);
  for (const need of info.required) {
    const found = need === "STOP" ? /\bSTOP\b/i.test(text) : used.includes(need);
    if (!found) {
      problems.push(need === "STOP" ? "Keep the line that tells them to reply STOP to opt out." : `Keep {${need}} in the message.`);
    }
  }
  return problems;
};

/** Reads the stored templates: only known ids, only real text. */
export const toMessageTemplates = (data: Record<string, unknown> | null | undefined): MessageTemplateOverrides => {
  const texts = data && typeof data.texts === "object" && data.texts !== null ? (data.texts as Record<string, unknown>) : {};
  const out: MessageTemplateOverrides = {};
  for (const id of MESSAGE_TEMPLATE_IDS) {
    const value = texts[id];
    if (typeof value === "string" && value.trim() && value.length <= MESSAGE_TEMPLATE_MAX_LENGTH) out[id] = value;
  }
  return out;
};
